/**
 * UtilityBillingPage.jsx — Nebenkostenabrechnung
 * Clara360 Frontend-Client für die Capital Atlas NK-Engine
 *
 * Sprint: CLARA360-NK-ABRECHNUNG-1 + CLARA360-UI-MOBILE-FIX-1 + NK-FRONTEND-BRIDGE-20260630
 *         + NK-VAULT-SYNC-AUTOMATION-1
 * API: /api/capital/trpc/* → Capital Atlas VPS (100.104.183.40:3006) via Nginx-Proxy
 *
 * Fixes (NK-FRONTEND-BRIDGE-20260630):
 *  - Nginx Port 3003 → 3006 korrigiert (Capital Atlas läuft auf 3006)
 *  - NkAbrechnungPanel: Fallback wenn Fulfillment-Expectations leer (Waldhofstraße)
 *  - SyncPanel: klare Auth-Fehlermeldung bei 401 (protectedProcedure)
 *  - Tabs: Fulfillment-Ansicht und NK-Kostenverteilung wählbar
 *
 * Fixes (NK-VAULT-SYNC-AUTOMATION-1):
 *  - SyncPanel: triggerSyncInternal (Service-Token, kein Manus-OAuth nötig)
 *  - SyncPanel: getSyncStatus zeigt letzten Sync-Zeitstempel
 *  - nk_sync_log: Persistenz für alle Sync-Läufe
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, RefreshCw, AlertCircle, CheckCircle, Clock,
  BarChart2, Database, Zap, ChevronDown, ChevronRight,
  ExternalLink, Info, TrendingUp, Building2, Layers, Euro, Lock
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { capitalFetch } from '../services/capitalApiService.js';

// ─── Property-Namen-Mapping (Supabase clara360) ──────────────────────────────
const PROPERTY_NAMES = {
  '8c16ffc6-a2e3-4c74-a799-0c45dc3ba0a2': 'Hafenpark Mannheim',
  'd0d19e39-8ba5-49f9-95e2-97670567cbcc': 'Bgm. Fuchs Str. 17',
  '812b06a2-cd17-423e-9179-d8ffe041afd3': 'Scheffelstraße 10',
  'b281fa26-9fce-47bf-9f1e-86b2a03f03ee': 'Wohnanlage Charlottenburg',
  '8f26ba2c-afb8-48b2-b3f7-3e20ac5af42d': 'Lagerhalle Spandau',
  'e963c246-7572-4bf6-88b2-c2a56a362a0b': 'Draistraße 26',
  'dc849495-81d2-487d-b1e7-394ca3403da3': 'Hauptstrasse 21 - Ilvesheim',
  '77044ffe-9398-40b6-a772-871d97f8814b': 'Waldhofstraße 76',
  '6dd5e99e-10c4-43fe-a420-de788f83b0ad': 'Janus HQ',
  '8dccc591-2305-44f7-aa3a-401fd3df5254': 'Mainzerstraße 120',
  'df8d4c41-f730-469a-bc2e-884b96fc7f92': 'Neckarauer Straße 45 WEG',
  '31c61013-8e7a-4bfd-b36a-eb94c40a579c': 'P7, 1',
  'dc26dcee-b168-49fa-9cef-d98a03d9aa1e': 'Geschäftshaus Kreuzberg',
  '3a100f4e-58f6-4df2-b99a-cf40e7b2da73': 'Mehrfamilienhaus Prenzlauer Berg',
  'b5ecfe61-9923-4c2d-bfcd-10e618254bc3': 'Office Center Mitte',
};

function getPropertyName(propertyId) {
  return PROPERTY_NAMES[propertyId] ?? `${propertyId.slice(0, 8)}…`;
}

// ─── API Helpers ────────────────────────────────────────────────────────────

const CAPITAL_BASE = '/api/capital/trpc';

async function capitalGet(procedure, input = null) {
  const url = input
    ? `${CAPITAL_BASE}/${procedure}?input=${encodeURIComponent(JSON.stringify({ json: input }))}`
    : `${CAPITAL_BASE}/${procedure}`;
  const res = await capitalFetch(url);
  if (!res.ok) throw new Error(`Capital API ${procedure}: HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.json?.message ?? 'Capital API Fehler');
  return data.result.data.json;
}


async function loadUnitsAndTenants(propertyId) {
  const { data: { session } } = await masterSupabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
  const response = await fetch(`/api/nk/units?property_id=${encodeURIComponent(propertyId)}`, {
    credentials: 'include',
    headers: {
      'x-supabase-access-token': accessToken,
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Einheitenabruf fehlgeschlagen (HTTP ${response.status})`);
  return payload;
}


async function loadUnitCostEvidence(propertyId, billingYear, costCode = 'building_insurance') {
  const { data: { session } } = await masterSupabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
  const params = new URLSearchParams({ property_id: propertyId, billing_year: String(billingYear), cost_code: costCode });
  const response = await fetch(`/api/nk/cost-evidence?${params.toString()}`, {
    credentials: 'include',
    headers: {
      'x-supabase-access-token': accessToken,
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Kostenfaktenabruf fehlgeschlagen (HTTP ${response.status})`);
  return payload;
}


async function loadReviewProperties() {
  const { data: { session } } = await masterSupabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
  const response = await fetch('/api/nk/review-properties', {
    credentials: 'include',
    headers: {
      'x-supabase-access-token': accessToken,
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Quellenreview-Abruf fehlgeschlagen (HTTP ${response.status})`);
  return payload;
}

async function capitalPost(procedure, input = {}) {
  const res = await capitalFetch(`${CAPITAL_BASE}/${procedure}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ json: input }),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('AUTH_REQUIRED');
    throw new Error(`Capital API ${procedure}: HTTP ${res.status}`);
  }
  const data = await res.json();
  if (data.error) throw new Error(data.error.json?.message ?? 'Capital API Fehler');
  return data.result.data.json;
}

// ─── Readiness Badge ─────────────────────────────────────────────────────────

function ReadinessBadge({ level }) {
  const config = {
    ready: { label: 'Bereit', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle },
    partial: { label: 'Teilweise', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
    incomplete: { label: 'Unvollständig', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', icon: AlertCircle },
    unknown: { label: 'Unbekannt', color: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400', icon: Info },
  };
  const c = config[level] ?? config.unknown;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// ─── Score Ring ───────────────────────────────────────────────────────────────

function ScoreRing({ score, size = 56 }) {
  const pct = (score != null && !isNaN(score)) ? Math.round(score * 100) : null;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = pct != null ? (pct / 100) * circ : 0;
  const color = pct == null ? '#9ca3af' : pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="6" className="dark:stroke-gray-700" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
}

// ─── Property Card ────────────────────────────────────────────────────────────

function PropertyCard({ propertyId, ledgerRows, billingYear, onSelect, isSelected }) {
  const [score, setScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(true);

  useEffect(() => {
    setLoadingScore(true);
    capitalGet('nkFulfillment.getScore', { propertyId, billingYear })
      .then(d => setScore(d.score))
      .catch(() => setScore(null))
      .finally(() => setLoadingScore(false));
  }, [propertyId, billingYear]);

  const propertyName = getPropertyName(propertyId);
  const pct = (score && score.overall_score != null && !isNaN(score.overall_score)) ? Math.round(score.overall_score * 100) : null;

  return (
    <button
      onClick={() => onSelect(propertyId)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
        isSelected
          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-400'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 hover:border-violet-300 dark:hover:border-violet-600'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className="w-4 h-4 text-violet-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{propertyName}</span>
        </div>
        {loadingScore ? (
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-slate-800 animate-pulse flex-shrink-0" />
        ) : (
          <div className="relative flex-shrink-0">
            <ScoreRing score={score?.overall_score} size={44} />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{
              color: pct == null ? '#9ca3af' : pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444'
            }}>
              {pct != null ? `${pct}%` : '–'}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <Database className="w-3 h-3" />
          <span>{ledgerRows} Buchungen</span>
        </div>
        {score ? (
          <ReadinessBadge level={score.readiness_level} />
        ) : (
          !loadingScore && <ReadinessBadge level="unknown" />
        )}
      </div>
    </button>
  );
}


function ReviewPropertyCard({ property, onSelect, isSelected }) {
  const propertyName = property.property_name || getPropertyName(property.property_id);
  return (
    <button
      type="button"
      onClick={() => onSelect(property.property_id)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
        isSelected
          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-400'
          : 'border-amber-200 dark:border-amber-900/70 bg-amber-50/40 dark:bg-slate-900 hover:border-amber-400 dark:hover:border-amber-600'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{propertyName}</span>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          <Clock className="h-3 w-3" />Quellenreview
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-300">
        {property.ledger_row_count || 0} Ledger-Zeilen · {property.unit_count || 0} Einheiten · {property.document_count || 0} Dokumentquellen
      </p>
      <p className="mt-1 text-[11px] text-amber-800/80 dark:text-amber-200/80">Dokumente prüfen, bevor eine Kostenbasis entstehen kann.</p>
    </button>
  );
}

// ─── Allgemeine Kostenarten-Referenz (betragsfrei, read-only) ────────────────

const GENERAL_NK_COST_REFERENCE = [
  // Reihenfolge entspricht der etablierten Abrechnungslogik; die Referenz bleibt betragsfrei.
  { label: 'Hauswart', costCode: 'house_caretaker', status: 'pruefen', hint: 'Verwaltung, Instandhaltung und Reparaturanteile abziehen' },
  { label: 'Gebäudeversicherung', costCode: 'building_insurance', status: 'umlagefaehig', hint: 'Versicherungsbeleg und Mietvertrag prüfen' },
  { label: 'Haftpflichtversicherung', costCode: 'liability_insurance', status: 'umlagefaehig', hint: 'Versicherungsbeleg und Mietvertrag prüfen' },
  { label: 'Müllabfuhr', costCode: 'waste_collection', status: 'umlagefaehig', hint: 'Laufende Entsorgungskosten' },
  { label: 'Wasserversorgung / Kaltwasser / Abwasser', costCode: 'water_bundle', detailMode: 'water', status: 'umlagefaehig', hint: 'BFW-Einheitswerte und Verbrauchszähler prüfen' },
  { label: 'Niederschlagswasser', costCode: 'rainwater', status: 'umlagefaehig', hint: 'Entwässerung und vertraglichen Schlüssel prüfen' },
  { label: 'Beleuchtung Treppenhaus / Allgemeinbeleuchtung', costCode: 'common_area_lighting', status: 'umlagefaehig', hint: 'Allgemeinstrom für Gemeinschaftsflächen' },
  { label: 'Betriebsmittel / Kleinteile', costCode: 'operating_supplies', status: 'pruefen', hint: 'Einzelbelege einer konkreten Kostenart zuordnen' },
  { label: 'Gebäudereinigung', status: 'umlagefaehig', hint: 'Laufende Reinigung von Gemeinschaftsflächen' },
  { label: 'Schädlingsbekämpfung', costCode: 'pest_control', status: 'pruefen', hint: 'Laufende Bekämpfung von einmaliger Schadensbeseitigung abgrenzen' },
  { label: 'Gartenpflege', status: 'umlagefaehig', hint: 'Laufende Pflege, keine Umgestaltung' },
  { label: 'Wartung Aufzug', costCode: 'elevator_maintenance', status: 'umlagefaehig', hint: 'Laufender Betrieb und Wartung, keine Reparatur' },
  { label: 'Schornsteinreinigung', status: 'umlagefaehig', hint: 'Soweit nicht bereits in Heizkosten enthalten' },
  { label: 'Wartung Brandschutz', costCode: 'fire_protection_maintenance', status: 'pruefen', hint: 'Laufende Wartung und Vertragsgrundlage prüfen' },
  { label: 'Wartung Rauchwarnmelder', status: 'pruefen', hint: 'Laufende Wartung und Vertragsgrundlage prüfen' },
  { label: 'Heizung / Warmwasser / BFW-Heizkostenabrechnung', costCode: 'bfw_heating_hotwater', status: 'verbrauch', hint: 'Verbrauch, Nutzungszeit und CO₂-Eigentümeranteil prüfen' },
  { label: 'Grundsteuer', costCode: 'property_tax', status: 'umlagefaehig', hint: 'Grundsteuerbescheid und Mietvertrag prüfen' },
  { label: 'Gemeinschaftsantenne / Glasfaseranlage', status: 'pruefen', hint: 'Gesetzliche und vertragliche Voraussetzungen prüfen' },
  { label: 'Gemeinschaftliche Wäscheeinrichtungen', status: 'umlagefaehig', hint: 'Nur Betrieb der Einrichtung' },
  { label: 'Sonstige Betriebskosten', status: 'pruefen', hint: 'Konkret, transparent und wirksam vereinbart' },
  { label: 'Reparaturen / Instandhaltung / Rücklage', status: 'ausgeschlossen', hint: 'Keine laufenden Betriebskosten' },
  { label: 'Verwaltung / Bankspesen / Sonderumlagen', status: 'ausgeschlossen', hint: 'Eigentümer- oder Verwaltungskosten' },
];

// Nur vorhandene Detailansichten; allgemeine Referenzkosten bleiben unverändert.
const NK_EXISTING_DETAIL_GROUPS = [
  {
    id: 'building_operations',
    title: 'Gebäude & Betrieb',
    labels: ['Hauswart', 'Gebäudeversicherung', 'Haftpflichtversicherung', 'Müllabfuhr', 'Niederschlagswasser', 'Beleuchtung Treppenhaus / Allgemeinbeleuchtung', 'Betriebsmittel / Kleinteile', 'Gebäudereinigung', 'Schädlingsbekämpfung'],
  },
  {
    id: 'water_heat',
    title: 'Wasser & Wärme',
    labels: ['Wasserversorgung / Kaltwasser / Abwasser', 'Heizung / Warmwasser / BFW-Heizkostenabrechnung', 'Schornsteinreinigung'],
  },
  {
    id: 'technical_safety',
    title: 'Technik & Sicherheit',
    labels: ['Wartung Aufzug', 'Wartung Brandschutz', 'Wartung Rauchwarnmelder', 'Gemeinschaftsantenne / Glasfaseranlage'],
  },
  {
    id: 'outdoor_community',
    title: 'Außenanlagen & Gemeinschaft',
    labels: ['Gartenpflege', 'Gemeinschaftliche Wäscheeinrichtungen'],
  },
  {
    id: 'levies',
    title: 'Abgaben',
    labels: ['Grundsteuer'],
  },
  {
    id: 'other_operating_costs',
    title: 'Weitere Betriebskosten',
    labels: ['Sonstige Betriebskosten'],
  },
];

function PropertyDetailedCostsPanel({ activeCostDetail, propertyId, billingYear, onOpenCostDetail, onCloseCostDetail, legendOnly = false }) {
  const [detailOrigin, setDetailOrigin] = useState(null);
  const [hoveredCostLabel, setHoveredCostLabel] = useState(null);
  const isEligibleObject = propertyId === 'df8d4c41-f730-469a-bc2e-884b96fc7f92' && Number(billingYear) === 2025;
  const detailGroups = NK_EXISTING_DETAIL_GROUPS.map((group) => ({
    ...group,
    rows: group.labels.map((label) => GENERAL_NK_COST_REFERENCE.find((row) => row.label === label)).filter(Boolean),
  }));
  const totalGroupedCosts = detailGroups.reduce((total, group) => total + group.rows.length, 0);
  if (!isEligibleObject) return null;
  const closeDetail = () => {
    setDetailOrigin(null);
    onCloseCostDetail();
  };
  const toggleDetail = (row) => {
    if (activeCostDetail?.code === row.costCode) {
      closeDetail();
      return;
    }
    setDetailOrigin('object');
    onOpenCostDetail({ code: row.costCode, label: row.label, detailMode: row.detailMode || 'single' });
  };
  const renderDetailPanel = (row) => row.detailMode === 'water'
    ? <WaterEvidenceDetailPanel propertyId={propertyId} billingYear={billingYear} onClose={closeDetail} />
    : <UnitCostEvidenceDetailPanel propertyId={propertyId} billingYear={billingYear} costCode={row.costCode} costLabel={row.label} onClose={closeDetail} />;
  return (
    <section className="rounded-lg border border-violet-200 dark:border-violet-800/70 bg-violet-50/30 dark:bg-violet-950/10 p-3">
      <div className="flex items-center justify-between gap-3"><div><h3 className="text-sm font-semibold text-violet-900 dark:text-violet-100">Kategorisierte NK-Kostenarten</h3><p className="mt-0.5 text-xs text-violet-700/80 dark:text-violet-300/80">Legende vor der Datenabfrage · Beträge und Einheitenwerte werden erst im Sammellauf geladen</p></div><span className="rounded-full bg-violet-100 dark:bg-violet-900/40 px-2.5 py-1 text-xs font-medium text-violet-700 dark:text-violet-200">{totalGroupedCosts} Kostenarten</span></div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {detailGroups.map((group) => <div key={group.id} className="rounded-md border border-violet-100 dark:border-violet-900/40 bg-white/85 dark:bg-slate-900/70 overflow-hidden"><div className="px-3 py-2 bg-violet-50/80 dark:bg-violet-900/15 flex items-center justify-between gap-2"><span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{group.title}</span><span className="text-[11px] text-violet-700 dark:text-violet-300">{group.rows.length}</span></div><div className="divide-y divide-violet-50 dark:divide-violet-900/30">{group.rows.map((row) => { const hasObjectDetail = !legendOnly && Boolean(row.costCode); return <div key={row.label} onMouseEnter={() => setHoveredCostLabel(row.label)} onMouseLeave={() => setHoveredCostLabel(null)}>{hasObjectDetail ? <button type="button" onClick={() => toggleDetail(row)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-violet-50/60 dark:hover:bg-violet-950/20 transition-colors"><span className="min-w-0 text-xs text-gray-700 dark:text-gray-200 truncate hover:text-violet-800 dark:hover:text-violet-100">{row.label}</span><ChevronRight className="w-3.5 h-3.5 shrink-0 text-violet-500" /></button> : <div className="flex items-center justify-between gap-2 px-3 py-2"><span className="min-w-0 text-xs text-gray-700 dark:text-gray-200 truncate">{row.label}</span></div>}{hoveredCostLabel === row.label ? <div className="border-t border-violet-100 dark:border-violet-900/40 bg-violet-50/70 dark:bg-violet-950/20 px-3 py-2 text-xs text-violet-900 dark:text-violet-100"><span className="font-semibold">Prüfhilfe:</span> {row.hint}</div> : null}{hasObjectDetail && detailOrigin === 'object' && activeCostDetail?.code === row.costCode ? <div className="border-t border-violet-100 dark:border-violet-900/40 bg-violet-50/30 dark:bg-violet-950/10 px-3 pb-3 pt-2">{renderDetailPanel(row)}</div> : null}</div>; })}</div></div>)}
      </div>
    </section>
  );
}
function GeneralNkCostReferencePanel({ propertyId }) {
  const [expanded, setExpanded] = useState(false);
  const [hoverHint, setHoverHint] = useState(null);
  const isNeckarauer = propertyId === 'df8d4c41-f730-469a-bc2e-884b96fc7f92';
  const statusConfig = {
    umlagefaehig: { label: 'Grundsätzlich umlegbar', color: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
    verbrauch: { label: 'Verbrauch / CO₂ prüfen', color: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
    pruefen: { label: 'Nur mit Prüfung', color: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
    ausgeschlossen: { label: 'Ausgeschlossen', color: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400' },
  };
  const rows = isNeckarauer
    ? GENERAL_NK_COST_REFERENCE.filter((row) => row.status === 'ausgeschlossen')
    : GENERAL_NK_COST_REFERENCE;
  const referenceTitle = isNeckarauer ? 'Nicht umlegbare Eigentümerkosten' : 'Kostenarten-Referenz: Was ist grundsätzlich umlegbar?';
  const referenceSubtitle = isNeckarauer ? 'Ausgeschlossen · nicht Teil der Mieter-NK-Abrechnung' : 'Allgemeine Orientierung · keine Objektbeträge · keine Mieterergebnisse';
  const referenceNotice = isNeckarauer ? 'Alle nicht ausgeschlossenen Kostenarten sind oberhalb in der gruppierten Objektansicht eingeordnet.' : 'Allgemeine Orientierung · keine Objektbeträge · keine Mieterergebnisse.';
  return (
    <div className="border border-violet-200 dark:border-violet-800/70 rounded-lg overflow-hidden">
      <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} className="w-full flex items-center justify-between gap-3 px-3 py-3 text-left bg-violet-50/70 dark:bg-violet-900/15 hover:bg-violet-100/70 dark:hover:bg-violet-900/25 transition-colors">
        <div className="min-w-0"><div className="flex items-center gap-2 text-sm font-semibold text-violet-800 dark:text-violet-200">{expanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}{referenceTitle}</div><p className="mt-1 text-xs text-violet-700/80 dark:text-violet-300/80">{referenceSubtitle}</p></div>
        <span className="text-xs text-violet-700 dark:text-violet-300 whitespace-nowrap">{rows.length} Kategorien</span>
      </button>
      {expanded && <div className="p-3 space-y-3 bg-white dark:bg-slate-900">
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-800 dark:text-blue-200">{referenceNotice}</div>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto"><table className="w-full text-xs min-w-[420px]"><thead><tr className="bg-gray-50 dark:bg-slate-800/50"><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Kostenart</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Einordnung</th></tr></thead><tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row) => { const config = statusConfig[row.status]; const isHintOpen = hoverHint === row.label; return <React.Fragment key={`${row.label}-hint`}><tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30"><td className="px-3 py-2 text-gray-800 dark:text-gray-200 font-medium"><span onMouseEnter={() => setHoverHint(row.label)} onMouseLeave={() => setHoverHint(null)} className="cursor-help hover:text-violet-800 dark:hover:text-violet-100 hover:underline decoration-violet-300 underline-offset-2">{row.label}</span></td><td className="px-3 py-2"><span className={`inline-flex items-center gap-1.5 ${config.color}`}><span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />{config.label}</span></td></tr>{isHintOpen ? <tr className="bg-violet-50/70 dark:bg-violet-950/20"><td colSpan={2} className="px-3 py-2.5 text-xs text-violet-900 dark:text-violet-100"><span className="font-semibold">Prüfhilfe:</span> {row.hint}</td></tr> : null}</React.Fragment>; })}
        </tbody></table></div>
      </div>}
    </div>
  );
}

// ─── Gebäudeversicherung – Detail-Canary (read-only) ─────────────────────────

function UnitCostEvidenceDetailPanel({ propertyId, billingYear, costCode, costLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    Promise.all([loadUnitsAndTenants(propertyId), loadUnitCostEvidence(propertyId, billingYear, costCode)])
      .then(([unitsPayload, evidencePayload]) => { if (active) setData({ units: unitsPayload.units || [], evidenceByUnit: Object.fromEntries((evidencePayload.facts || []).map((fact) => [fact.unit_id, fact])), evidenceSummary: evidencePayload.summary || {} }); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId, billingYear, costCode]);
  const fmt = (value) => value == null ? '–' : `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  return <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-3 bg-blue-50/40 dark:bg-blue-900/10">
    <div className="flex items-start justify-between gap-3"><div><div className="text-sm font-semibold text-blue-800 dark:text-blue-200">{costLabel} · Detailansicht</div><p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">Abrechnungsjahr {billingYear} · MDS-WEG-Anteil je Einheit</p></div><button type="button" onClick={onClose} className="text-xs text-blue-700 dark:text-blue-300 hover:underline whitespace-nowrap">Schließen</button></div>
    <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">Die ausgewiesenen Werte sind MDS-WEG-/Eigentümeranteile der Einheiten. Sie sind keine Mieterforderungen. Der Mieteranteil bleibt gesperrt, bis Mietvertrag, Umlagevereinbarung und Mietzeitraum {billingYear} geprüft sind.</div>
    <div className="grid grid-cols-3 gap-3"><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-blue-700 dark:text-blue-300">{data?.evidenceSummary?.fact_count ?? '–'}</div><div className="text-[11px] text-gray-500 dark:text-gray-400">MDS-Einheitsfakten</div></div><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-blue-700 dark:text-blue-300">{fmt(data?.evidenceSummary?.owner_cost_share_sum_eur)}</div><div className="text-[11px] text-gray-500 dark:text-gray-400">WEG-Anteile</div></div><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-amber-700 dark:text-amber-300">0,00 €</div><div className="text-[11px] text-amber-600 dark:text-amber-400">Mieteranteile gesperrt</div></div></div>
    {loading && <div className="space-y-2 animate-pulse">{[1, 2, 3].map((item) => <div key={item} className="h-10 bg-white dark:bg-slate-800 rounded-md" />)}</div>}
    {error && <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2">{error}</div>}
    {!loading && !error && data?.units?.length > 0 && <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden overflow-x-auto bg-white dark:bg-slate-900"><table className="w-full text-xs min-w-[860px]"><thead><tr className="bg-gray-50 dark:bg-slate-800/50"><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Einheit</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Aktueller Mieter</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">MDS-WEG-Anteil {costLabel}</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Evidence</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Mieteranteil</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Status</th></tr></thead><tbody className="divide-y divide-gray-100 dark:divide-gray-700">{data.units.map((unit) => { const evidence = data.evidenceByUnit[unit.unit_id]; return <tr key={unit.unit_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30"><td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{unit.unit_identifier}</td><td className="px-3 py-2 text-gray-700 dark:text-gray-300">{unit.tenant_name || '–'}</td><td className="px-3 py-2 text-right font-medium text-blue-700 dark:text-blue-300">{evidence ? fmt(evidence.owner_cost_share_eur) : 'Keine MDS-Evidence'}</td><td className="px-3 py-2 text-gray-500 dark:text-gray-400">{evidence ? `MDS Einzelnachweis S. ${evidence.evidence_page} · kombinierte S. ${evidence.combined_pdf_page}` : '–'}</td><td className="px-3 py-2 text-gray-500 dark:text-gray-400">Noch nicht berechnet</td><td className="px-3 py-2 text-right"><span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${evidence ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400'}`}>{evidence ? 'Mieterbasis prüfen' : 'Keine Evidence'}</span></td></tr>; })}</tbody></table></div>}
    <p className="text-[11px] text-gray-500 dark:text-gray-400">Quelle: Capital MDS-WEG-Einheitsfakten. Diese Ansicht erzeugt keine Forderung, keinen Saldo und keine Buchung.</p>
  </div>;
}

// ─── BFW Wasser – Detail-Canary (read-only, keine Mieterabrechnung) ───────────

const WATER_EVIDENCE_GROUPS = [
  { code: 'water_sewer', label: 'Wasser / Abwasser' },
  { code: 'coldwater_billing', label: 'Kaltwasser-Abrechnung' },
  { code: 'coldwater_meter_service', label: 'KWZ-Service' },
];

function WaterEvidenceDetailPanel({ propertyId, billingYear, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    Promise.all([loadUnitsAndTenants(propertyId), ...WATER_EVIDENCE_GROUPS.map((group) => loadUnitCostEvidence(propertyId, billingYear, group.code))])
      .then(([unitsPayload, ...payloads]) => {
        if (!active) return;
        const evidenceByUnit = {};
        (unitsPayload.units || []).forEach((unit) => { evidenceByUnit[unit.unit_id] = {}; });
        payloads.forEach((payload, index) => {
          const code = WATER_EVIDENCE_GROUPS[index].code;
          (payload.facts || []).forEach((fact) => {
            if (!evidenceByUnit[fact.unit_id]) evidenceByUnit[fact.unit_id] = {};
            evidenceByUnit[fact.unit_id][code] = fact;
          });
        });
        const totalOwnerShare = payloads.reduce((sum, payload) => sum + Number(payload.summary?.owner_cost_share_sum_eur || 0), 0);
        const factCount = payloads.reduce((sum, payload) => sum + Number(payload.summary?.fact_count || 0), 0);
        setData({ units: unitsPayload.units || [], evidenceByUnit, totalOwnerShare, factCount });
      })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId, billingYear]);
  const fmtEur = (value) => value == null ? '–' : `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtM3 = (value) => value == null ? '–' : `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m³`;
  return <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-3 bg-blue-50/40 dark:bg-blue-900/10">
    <div className="flex items-start justify-between gap-3"><div><div className="text-sm font-semibold text-blue-800 dark:text-blue-200">Wasserversorgung / Kaltwasser / Abwasser · Detailansicht</div><p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">Abrechnungsjahr {billingYear} · BFW-WEG-/Eigentümer-Evidence je Einheit</p></div><button type="button" onClick={onClose} className="text-xs text-blue-700 dark:text-blue-300 hover:underline whitespace-nowrap">Schließen</button></div>
    <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">Die drei Wassergruppen sind beleggebundene BFW-WEG-/Eigentümeranteile. Sie sind keine Mieterforderungen; Mieteranteile, Vorauszahlungen und Abrechnung bleiben gesperrt.</div>
    <div className="grid grid-cols-3 gap-3"><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-blue-700 dark:text-blue-300">{data?.factCount ?? '–'}</div><div className="text-[11px] text-gray-500 dark:text-gray-400">BFW-Einheitsfakten</div></div><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-blue-700 dark:text-blue-300">{fmtEur(data?.totalOwnerShare)}</div><div className="text-[11px] text-gray-500 dark:text-gray-400">WEG-Anteile Wasserblock</div></div><div className="bg-white dark:bg-slate-900 rounded-md p-2.5 text-center border border-blue-100 dark:border-blue-900/60"><div className="text-sm font-bold text-amber-700 dark:text-amber-300">0,00 €</div><div className="text-[11px] text-amber-600 dark:text-amber-400">Mieteranteile gesperrt</div></div></div>
    {loading && <div className="space-y-2 animate-pulse">{[1, 2, 3].map((item) => <div key={item} className="h-10 bg-white dark:bg-slate-800 rounded-md" />)}</div>}
    {error && <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2">{error}</div>}
    {!loading && !error && data?.units?.length > 0 && <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden overflow-x-auto bg-white dark:bg-slate-900"><table className="w-full text-xs min-w-[1180px]"><thead><tr className="bg-gray-50 dark:bg-slate-800/50"><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Einheit</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Aktueller Mieter</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Wasser / Abwasser</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Kaltwasser-Abrechnung</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">KWZ-Service</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Kalt / Warmwasser</th><th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Wasserblock WEG</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Evidence</th><th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Mieteranteil</th></tr></thead><tbody className="divide-y divide-gray-100 dark:divide-gray-700">{data.units.map((unit) => { const facts = data.evidenceByUnit[unit.unit_id] || {}; const water = facts.water_sewer; const billing = facts.coldwater_billing; const service = facts.coldwater_meter_service; const usage = water?.water_usage || billing?.water_usage || service?.water_usage; const complete = WATER_EVIDENCE_GROUPS.every((group) => facts[group.code]); const total = [water, billing, service].reduce((sum, fact) => sum + Number(fact?.owner_cost_share_eur || 0), 0); return <tr key={unit.unit_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30"><td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{unit.unit_identifier}</td><td className="px-3 py-2 text-gray-700 dark:text-gray-300">{unit.tenant_name || '–'}</td><td className="px-3 py-2 text-right font-medium text-blue-700 dark:text-blue-300">{water ? fmtEur(water.owner_cost_share_eur) : '–'}</td><td className="px-3 py-2 text-right font-medium text-blue-700 dark:text-blue-300">{billing ? fmtEur(billing.owner_cost_share_eur) : '–'}</td><td className="px-3 py-2 text-right font-medium text-blue-700 dark:text-blue-300">{service ? fmtEur(service.owner_cost_share_eur) : '–'}</td><td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300"><div>{fmtM3(usage?.coldwater_consumption_m3)}</div><div className="text-[11px] text-gray-500 dark:text-gray-400">warm {fmtM3(usage?.warmwater_consumption_m3)}</div></td><td className="px-3 py-2 text-right font-semibold text-blue-800 dark:text-blue-200">{complete ? fmtEur(total) : '–'}</td><td className="px-3 py-2 text-gray-500 dark:text-gray-400">{complete ? <div className="space-y-0.5">{WATER_EVIDENCE_GROUPS.map((group) => <div key={group.code}>{group.label}: BFW S. {facts[group.code]?.evidence_page ?? '–'}</div>)}</div> : 'Evidence unvollständig'}</td><td className="px-3 py-2 text-gray-500 dark:text-gray-400">Noch nicht berechnet</td></tr>; })}</tbody></table></div>}
    <p className="text-[11px] text-gray-500 dark:text-gray-400">Quelle: Capital BFW-Einheitsfakten. Diese Ansicht erzeugt keine Forderung, keinen Saldo und keine Buchung.</p>
  </div>;
}

// ─── NK-Abrechnung Panel (Kostenverteilung) ───────────────────────────────────

function NkAbrechnungPanel({ propertyId, billingYear }) {
  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-violet-200 dark:border-violet-800/70 bg-violet-50/50 dark:bg-violet-950/10 p-4">
        <div className="flex items-start gap-3"><Info className="mt-0.5 h-5 w-5 shrink-0 text-violet-600 dark:text-violet-300" /><div><h2 className="text-sm font-semibold text-violet-900 dark:text-violet-100">NK-Kostenarten · Legende vor Datenabfrage</h2><p className="mt-1 text-xs leading-relaxed text-violet-800/85 dark:text-violet-200/85">Hier werden zunächst nur die Kostenarten und ihre Einordnung gezeigt. Es werden auf dieser Objektseite keine WEG-Beträge, Einheitenwerte, Mieteranteile oder Salden geladen. Mit <strong>„NK-Abrechnung“</strong> unten rechts öffnen Sie den Jahresstart; dort wählen Sie das Jahr und starten anschließend <strong>„Daten sammeln“</strong>.</p></div></div>
      </section>
      <PropertyDetailedCostsPanel
        legendOnly
        activeCostDetail={null}
        propertyId={propertyId}
        billingYear={billingYear}
        onOpenCostDetail={() => {}}
        onCloseCostDetail={() => {}}
      />
      <GeneralNkCostReferencePanel propertyId={propertyId} />
    </div>
  );
}

// ─── Expectations Panel ───────────────────────────────────────────────────────

function ExpectationsPanel({ propertyId, billingYear }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    capitalGet('nkFulfillment.getExpectations', { propertyId, billingYear })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [propertyId, billingYear]);

  if (loading) return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 dark:bg-slate-800 rounded-lg" />)}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      {error}
    </div>
  );

  if (!data || data.total === 0) return (
    <div className="text-center py-6 text-gray-500 dark:text-gray-400">
      <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">Keine Vault-Expectations für dieses Objekt.</p>
      <p className="text-xs mt-1 opacity-70">Sync auslösen um Vault-Daten (VPS165) zu laden.</p>
    </div>
  );

  const statusConfig = {
    fulfilled: { label: 'Erfüllt', color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
    missing: { label: 'Fehlend', color: 'text-red-600 dark:text-red-400', dot: 'bg-red-500' },
    needs_bank_evidence: { label: 'Bank fehlt', color: 'text-orange-600 dark:text-orange-400', dot: 'bg-orange-500' },
    estimated: { label: 'Geschätzt', color: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
    conflict: { label: 'Konflikt', color: 'text-red-700 dark:text-red-300', dot: 'bg-red-700' },
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{data.fulfilledCount}</div>
          <div className="text-xs text-emerald-600 dark:text-emerald-400">Erfüllt</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-700 dark:text-red-300">{data.missingMandatory.length}</div>
          <div className="text-xs text-red-600 dark:text-red-400">Pflicht fehlt</div>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{data.total}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Gesamt</div>
        </div>
      </div>

      {data.missingMandatory.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="text-sm font-semibold text-red-700 dark:text-red-300">
              Pflichtbelege fehlen ({data.missingMandatory.length})
            </span>
          </div>
          <ul className="space-y-1">
            {data.missingMandatory.slice(0, 5).map((e, i) => (
              <li key={i} className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                {e.expectation_label ?? e.cost_type ?? e.id}
              </li>
            ))}
            {data.missingMandatory.length > 5 && (
              <li className="text-xs text-red-500 dark:text-red-400 opacity-70">
                + {data.missingMandatory.length - 5} weitere…
              </li>
            )}
          </ul>
        </div>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Alle {data.total} Positionen
      </button>

      {expanded && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-xs min-w-[300px]">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/50">
                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Position</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Pflicht</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {data.expectations.map((e, i) => {
                const sc = statusConfig[e.fulfillment_status] ?? { label: e.fulfillment_status, color: 'text-gray-500', dot: 'bg-gray-400' };
                return (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {e.expectation_label ?? e.cost_type ?? `Position ${i + 1}`}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {e.is_mandatory ? (
                        <span className="text-red-500 font-bold">✓</span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">–</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`inline-flex items-center gap-1 ${sc.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── Einheiten & Mieter (read-only) ─────────────────────────────────────────
function UnitsAndTenantsPanel({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadUnitsAndTenants(propertyId)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propertyId]);

  const fmtMoney = (value) => value != null
    ? `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : '–';
  const locationLabel = (unit) => {
    const name = String(unit.unit_identifier || '');
    if (/maisonette/i.test(name)) return 'Maisonette';
    if (/\bdg\b/i.test(name)) return 'DG';
    if (String(unit.floor) === '0') return 'EG';
    return unit.floor != null ? `${unit.floor}. OG` : '–';
  };

  if (loading) return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3, 4].map((item) => <div key={item} className="h-12 bg-gray-100 dark:bg-slate-800 rounded-lg" />)}
    </div>
  );
  if (error) return (
    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      {error}
    </div>
  );
  if (!data || data.units?.length === 0) return (
    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">Keine Einheiten für dieses Objekt hinterlegt.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-violet-600 dark:text-violet-300 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-violet-800 dark:text-violet-200">Aktuelle Einheiten- und Mieterbasis</div>
            <p className="text-xs text-violet-700 dark:text-violet-300 mt-0.5">
              Read-only Capital-Stammdaten. Die Angaben sind keine historische Belegungs- oder Vorauszahlungsbestätigung für eine Mieter-NK-Abrechnung.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-violet-700 dark:text-violet-300">{data.summary?.total_units ?? data.units.length}</div>
          <div className="text-xs text-violet-600 dark:text-violet-400">Einheiten</div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{data.summary?.occupied_units ?? 0}</div>
          <div className="text-xs text-emerald-600 dark:text-emerald-400">Belegt</div>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-gray-700 dark:text-gray-300">{data.summary?.vacant_units ?? 0}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Leerstand</div>
        </div>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-xs min-w-[720px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-800/50">
              <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Einheit</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Lage</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Fläche</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Aktueller Mieter</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Belegung</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-400">Aktuelle NK-VZ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.units.map((unit) => (
              <tr key={unit.unit_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{unit.unit_identifier}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{locationLabel(unit)}</td>
                <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{unit.size_sqm != null ? `${Number(unit.size_sqm).toLocaleString('de-DE')} m²` : '–'}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{unit.tenant_name || <span className="text-gray-400">–</span>}</td>
                <td className="px-3 py-2 text-center">
                  {unit.is_occupied ? (
                    <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Belegt</span>
                  ) : (
                    <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-300">Leerstand</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{fmtMoney(unit.utilities_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">Quelle: Capital · `property_units` · schreibgeschützte Ansicht</p>
    </div>
  );
}

// ─── Detail Panel mit Tabs ────────────────────────────────────────────────────

function DetailPanel({ propertyId, billingYear, refreshKey, onOpenNkArchive }) {
  const [activeTab, setActiveTab] = useState('abrechnung');

  useEffect(() => {
    setActiveTab('abrechnung');
  }, [propertyId]);

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Layers className="w-4 h-4 text-violet-500" />
          <span className="truncate">
            {getPropertyName(propertyId)} — {billingYear}
          </span>
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('abrechnung')}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
            activeTab === 'abrechnung'
              ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-b-2 border-violet-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-1">
            <Euro className="w-3 h-3" />
            NK-Kostenverteilung
          </span>
        </button>
        <button
          onClick={() => setActiveTab('units')}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
            activeTab === 'units'
              ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-b-2 border-violet-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            Einheiten & Mieter
          </span>
        </button>
        <button
          onClick={() => setActiveTab('fulfillment')}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
            activeTab === 'fulfillment'
              ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-b-2 border-violet-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Vault-Fulfillment
          </span>
        </button>
      </div>

      {activeTab === 'abrechnung' ? (
        <NkAbrechnungPanel
          key={`abr-${propertyId}-${billingYear}-${refreshKey}`}
          propertyId={propertyId}
          billingYear={billingYear}
        />
      ) : activeTab === 'units' ? (
        <UnitsAndTenantsPanel
          key={`units-${propertyId}-${refreshKey}`}
          propertyId={propertyId}
        />
      ) : (
        <ExpectationsPanel
          key={`exp-${propertyId}-${billingYear}-${refreshKey}`}
          propertyId={propertyId}
          billingYear={billingYear}
        />
      )}

      {/* Read-only statement archive entry */}
      <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nächster Schritt: Jahr wählen und vorhandene Cloud-, Vault- und Capital-Daten im Sammellauf read-only suchen.
          </p>
          <button type="button" onClick={() => onOpenNkArchive(propertyId, billingYear)} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 whitespace-nowrap">
            <FileText className="w-3.5 h-3.5" />NK-Abrechnung
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sync Panel ───────────────────────────────────────────────────────────────

// Service-Token für internen Sync (kein Manus-OAuth erforderlich)
const NK_SYNC_TOKEN = import.meta.env.VITE_NK_SYNC_TOKEN || '119e49b2757c4c99475b90329f2df431d8282df1a2e9ac51f6958bbbf0ae8fca';

function SyncPanel({ onSynced }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Letzten Sync-Status beim Laden abrufen
  useEffect(() => {
    capitalGet('nkFulfillment.getSyncStatus')
      .then(d => setSyncStatus(d))
      .catch(() => setSyncStatus(null))
      .finally(() => setLoadingStatus(false));
  }, [result]);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      // Versuche zuerst den internen Service-Token-Sync
      const r = await capitalPost('nkFulfillment.triggerSyncInternal', { service_token: NK_SYNC_TOKEN });
      setResult(r);
      if (r.success && onSynced) onSynced();
    } catch (e) {
      // Fallback: protectedProcedure (Capital Atlas Login)
      if (e.message?.includes('NK_SYNC_UNAUTHORIZED')) {
        try {
          const r2 = await capitalPost('nkFulfillment.triggerSync');
          setResult(r2);
          if (r2.success && onSynced) onSynced();
        } catch (e2) {
          if (e2.message === 'AUTH_REQUIRED') {
            setResult({
              success: false,
              pass: false,
              output: 'Sync erfordert Capital Atlas Login. Bitte zuerst auf capital.atlas-os.ai anmelden.',
              authRequired: true,
            });
          } else {
            setResult({ success: false, pass: false, output: e2.message });
          }
        }
      } else {
        setResult({ success: false, pass: false, output: e.message });
      }
    } finally {
      setSyncing(false);
    }
  };

  // Zeitstempel formatieren
  const formatSyncTime = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const lastSync = syncStatus?.last_sync;
  const vaultAvailable = syncStatus?.vault_available;

  return (
    <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Fulfillment-Sync</span>
          {!loadingStatus && vaultAvailable && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
              <CheckCircle className="w-2.5 h-2.5" /> Vault aktiv
            </span>
          )}
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synchronisiere…' : 'Sync starten'}
        </button>
      </div>

      {/* Letzter Sync-Zeitstempel */}
      {!loadingStatus && lastSync && (
        <div className="flex items-center gap-2 mb-3 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3" />
          <span>Letzter Sync: {formatSyncTime(lastSync.synced_at)}</span>
          {lastSync.pass && <span className="text-emerald-600 dark:text-emerald-400">✓ PASS</span>}
          <span className="opacity-60">· {lastSync.scores_synced} Scores · {lastSync.expectations_synced} Exp.</span>
        </div>
      )}
      {!loadingStatus && !lastSync && (
        <div className="flex items-center gap-2 mb-3 text-xs text-amber-600 dark:text-amber-400">
          <AlertCircle className="w-3 h-3" />
          <span>Noch kein Sync durchgeführt</span>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Synchronisiert Vault-Daten (VPS165) mit der Capital Atlas NK-Engine. Dauert ca. 10–30 Sekunden.
      </p>

      {result && (
        <div className={`rounded-lg p-3 text-xs font-medium ${
          result.pass
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : result.authRequired
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-2">
            {result.authRequired ? (
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            ) : result.pass ? (
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            )}
            <div>
              {result.authRequired ? (
                <>
                  <div className="font-semibold mb-1">Capital Atlas Login erforderlich</div>
                  <div className="opacity-80">{result.output}</div>
                  <a
                    href="https://capital.atlas-os.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1.5 underline hover:no-underline"
                  >
                    capital.atlas-os.ai öffnen <ExternalLink className="w-3 h-3" />
                  </a>
                </>
              ) : (
                <div>
                  <div>{result.pass ? '✓ PASS' : '✗ FEHLER'}</div>
                  {result.scores_synced != null && (
                    <div className="opacity-70 mt-0.5">{result.scores_synced} Scores · {result.expectations_synced} Expectations synchronisiert</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const UtilityBillingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPropertyId = searchParams.get('property_id');
  const requestedBillingYear = Number(searchParams.get('billing_year'));
  const [ledgerCounts, setLedgerCounts] = useState([]);
  const [reviewProperties, setReviewProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(requestedPropertyId || null);
  const [billingYear, setBillingYear] = useState(Number.isInteger(requestedBillingYear) && requestedBillingYear >= 2000 && requestedBillingYear <= 2100 ? requestedBillingYear : 2025);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (requestedPropertyId) setSelectedPropertyId(requestedPropertyId);
    if (Number.isInteger(requestedBillingYear) && requestedBillingYear >= 2000 && requestedBillingYear <= 2100) setBillingYear(requestedBillingYear);
  }, [requestedPropertyId, requestedBillingYear]);
  const openNkArchive = useCallback((propertyId, year) => {
    navigate(`/utility-billing/abrechnungen?property_id=${encodeURIComponent(propertyId)}&billing_year=${encodeURIComponent(year)}`);
  }, [navigate]);

  const loadLedgerCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await capitalGet('nkFulfillment.getActiveLedgerCounts');
      setLedgerCounts(data.counts ?? []);
      try {
        const reviewData = await loadReviewProperties();
        setReviewProperties(reviewData.review_properties ?? []);
      } catch (reviewError) {
        console.warn('[NK] Quellenreview konnte nicht geladen werden:', reviewError.message);
        setReviewProperties([]);
      }
      if (data.counts?.length > 0 && !selectedPropertyId) {
        setSelectedPropertyId(data.counts[0].property_id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLedgerCounts();
  }, [loadLedgerCounts, refreshKey]);

  const totalLedgerRows = ledgerCounts.reduce((s, c) => s + c.active_rows, 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5 md:w-6 md:h-6 text-violet-500" />
              Nebenkostenabrechnung
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Powered by Capital Atlas NK-Engine
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={billingYear}
              onChange={e => setBillingYear(Number(e.target.value))}
              className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {[2023, 2024, 2025, 2026].map(y => (
                <option key={y} value={y}>Abrechnungsjahr {y}</option>
              ))}
            </select>
            <a
              href="https://capital.atlas-os.ai/nk-abrechnung"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-700 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Capital Atlas
            </a>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{ledgerCounts.length}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Objekte mit aktiver Kostenbasis</div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalLedgerRows.toLocaleString('de-DE')}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Aktive Ledger-Zeilen</div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{billingYear}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Abrechnungsjahr</div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-sm">Capital Atlas nicht erreichbar</div>
            <div className="text-xs mt-0.5 opacity-80">{error}</div>
          </div>
          <button onClick={loadLedgerCounts} className="ml-auto text-xs underline hover:no-underline">Erneut versuchen</button>
        </div>
      )}

      {/* Main Layout */}
      <div className="flex flex-col md:grid md:grid-cols-12 gap-4 md:gap-6">
        {/* Left: Property List */}
        <div className="md:col-span-4 space-y-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-violet-500" />
              Objekte ({ledgerCounts.length})
            </h2>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-gray-100 dark:bg-slate-800 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : ledgerCounts.length === 0 ? (
              <div className="text-center py-6 text-gray-400 dark:text-gray-500">
                <Database className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Keine Objekte gefunden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {ledgerCounts.map(({ property_id, active_rows }) => (
                  <PropertyCard
                    key={property_id}
                    propertyId={property_id}
                    ledgerRows={active_rows}
                    billingYear={billingYear}
                    onSelect={setSelectedPropertyId}
                    isSelected={selectedPropertyId === property_id}
                  />
                ))}
              </div>
            )}
          </div>

          {!loading && reviewProperties.length > 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-amber-200 dark:border-amber-900/60 p-4">
              <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1 flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Quellenreview ({reviewProperties.length})
              </h2>
              <p className="mb-3 text-xs text-amber-800/80 dark:text-amber-200/80">Originalquellen sind vorhanden; eine fachlich bestätigte Kostenbasis fehlt noch.</p>
              <div className="space-y-2">
                {reviewProperties.map((property) => (
                  <ReviewPropertyCard
                    key={property.property_id}
                    property={property}
                    onSelect={setSelectedPropertyId}
                    isSelected={selectedPropertyId === property.property_id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sync */}
          <SyncPanel onSynced={() => setRefreshKey(k => k + 1)} />
        </div>

        {/* Right: Detail */}
        <div className="md:col-span-8">
          {selectedPropertyId ? (
            <DetailPanel
              key={`detail-${selectedPropertyId}-${billingYear}`}
              propertyId={selectedPropertyId}
              billingYear={billingYear}
              refreshKey={refreshKey}
              onOpenNkArchive={openNkArchive}
            />
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <Building2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">Objekt auswählen</h3>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Wähle ein Objekt aus der Liste, um die NK-Daten zu sehen.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtilityBillingPage;
